<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { i18n } from '@/i18n';
import { CalendarDay, useCalendar } from '@/corePagesModule/composables/useCalendar';
import Mdi from './Mdi.vue';

const t = i18n.t;

const props = withDefaults(
  defineProps<{
    modelValue?: Date | null;
    firstDayOfWeek?: 0 | 1 | 2 | 3 | 4 | 5 | 6;
    hideTime?: boolean;
  }>(),
  {
    modelValue: null,
    firstDayOfWeek: 0,
    hideTime: false,
  }
);

const emit = defineEmits<{
  (e: 'update:modelValue', value: Date | null): void;
  (e: 'change-month', payload: { year: number; month: number }): void;
}>();

const selectedDate = ref<Date | null>(props.modelValue ? new Date(props.modelValue) : null);

/**
 * Dateをタイムスタンプに変換する。
 * @param value - 変換対象
 */
const toTimestamp = (value?: Date | null): number | null => {
  return value ? value.getTime() : null;
};

const { weeks, headerLabel, displayYearMonth, moveMonth, selectDate, clearSelection, goToday } = useCalendar({
  selectedDate,
  firstDayOfWeek: props.firstDayOfWeek,
});

watch(
  () => props.modelValue,
  (value) => {
    selectedDate.value = value ? new Date(value) : null;
  }
);

watch(selectedDate, (value) => {
  // 同じタイムスタンプの場合は親へ再送出しない
  if (toTimestamp(value) === toTimestamp(props.modelValue ?? null)) return;
  emit('update:modelValue', value ? new Date(value) : null);
});

watch(
  displayYearMonth,
  (value) => {
    emit('change-month', { year: value.year, month: value.month });
  },
  { immediate: true }
);

const dayNames = computed(() => [t('曜日:日'), t('曜日:月'), t('曜日:火'), t('曜日:水'), t('曜日:木'), t('曜日:金'), t('曜日:土')]);
const todayLabel = computed(() => t('今日'));
const deleteLabel = computed(() => t('削除'));
const timeTitle = computed(() => t('時間'));
const hourLabel = computed(() => t('時'));
const minuteLabel = computed(() => t('分'));
const secondLabel = computed(() => t('秒'));

const hours = computed(() => Array.from({ length: 24 }, (_, i) => i));
const minutes = computed(() => Array.from({ length: 60 }, (_, i) => i));
const seconds = computed(() => Array.from({ length: 60 }, (_, i) => i));

const selectedHour = computed(() => (selectedDate.value ? selectedDate.value.getHours() : 0));
const selectedMinute = computed(() => (selectedDate.value ? selectedDate.value.getMinutes() : 0));
const selectedSecond = computed(() => (selectedDate.value ? selectedDate.value.getSeconds() : 0));

/**
 * 日付選択時のハンドラ。
 * @param day - 押下された日付情報
 */
const handleSelectDay = (day: CalendarDay) => {
  selectDate(day.date);
};

/**
 * 月移動ボタンのハンドラ。
 * @param diff - 増減させる月数
 */
const handleMoveMonth = (diff: number) => {
  moveMonth(diff);
};

/**
 * 選択解除ボタンのハンドラ。
 */
const handleClear = () => {
  clearSelection();
};

/**
 * 今日へ移動するハンドラ。
 */
const handleToday = () => {
  goToday();
};

/**
 * 選択中の日付に時刻を設定する。
 * @param hour - 設定する時
 * @param minute - 設定する分
 * @param second - 設定する秒
 */
const applyTime = (hour: number, minute: number, second: number) => {
  const base = selectedDate.value ? new Date(selectedDate.value) : new Date();
  base.setHours(hour, minute, second, 0);
  selectedDate.value = base;
};

/**
 * 時の選択時に時刻を更新する。
 * @param hour - 設定する時
 */
const handleSelectHour = (hour: number) => {
  applyTime(hour, selectedMinute.value, selectedSecond.value);
};

/**
 * 分の選択時に時刻を更新する。
 * @param minute - 設定する分
 */
const handleSelectMinute = (minute: number) => {
  applyTime(selectedHour.value, minute, selectedSecond.value);
};

/**
 * 秒の選択時に時刻を更新する。
 * @param second - 設定する秒
 */
const handleSelectSecond = (second: number) => {
  applyTime(selectedHour.value, selectedMinute.value, second);
};
</script>

<template>
  <div class="lp-calendar" :class="{ 'lp-calendar--date-only': hideTime }">
    <div class="lp-calendar__panel">
      <div class="lp-calendar__header">
        <div class="lp-calendar__title">
          <span class="lp-calendar__title-text">
            {{ headerLabel }}
          </span>
        </div>
        <div class="lp-calendar__nav">
          <button class="lp-calendar__nav-button" type="button" @click="handleMoveMonth(-1)">
            <Mdi icon="arrow_upward" aria-label="prev-month" />
          </button>
          <button class="lp-calendar__nav-button" type="button" @click="handleMoveMonth(1)">
            <Mdi icon="arrow_downward" aria-label="next-month" />
          </button>
        </div>
      </div>
      <div class="lp-calendar__grid">
        <div
          v-for="dayName in dayNames"
          :key="dayName"
          class="lp-calendar__weekday"
          :class="{
            'lp-calendar__weekday--sunday': dayName === t('曜日:日'),
            'lp-calendar__weekday--saturday': dayName === t('曜日:土'),
          }"
        >
          {{ dayName }}
        </div>
        <template v-for="week in weeks" :key="`${week.weekNumber}-${week.days[0].date.toISOString()}`">
          <button
            v-for="day in week.days"
            :key="day.date.toISOString()"
            type="button"
            class="lp-calendar__day"
            :class="{
              'lp-calendar__day--other': !day.isCurrentMonth,
              'lp-calendar__day--sunday': day.isCurrentMonth && day.date.getDay() === 0,
              'lp-calendar__day--saturday': day.isCurrentMonth && day.date.getDay() === 6,
              'lp-calendar__day--selected': day.isSelected,
              'lp-calendar__day--today': day.isToday && !day.isSelected,
            }"
            @click="handleSelectDay(day)"
          >
            {{ day.date.getDate() }}
          </button>
        </template>
      </div>
      <div class="lp-calendar__footer">
        <button class="lp-calendar__link-button" type="button" @click="handleClear">
          {{ deleteLabel }}
        </button>
        <button class="lp-calendar__link-button lp-calendar__link-button--today" type="button" @click="handleToday">
          {{ todayLabel }}
        </button>
      </div>
    </div>
    <div v-if="!hideTime" class="lp-calendar__time">
      <div class="lp-calendar__time-title">{{ timeTitle }}</div>
      <div class="lp-calendar__time-columns">
        <div class="lp-calendar__time-column">
          <div class="lp-calendar__time-label">{{ hourLabel }}</div>
          <div class="lp-calendar__time-scroll">
            <button
              v-for="hour in hours"
              :key="`hour-${hour}`"
              type="button"
              class="lp-calendar__time-cell"
              :class="{ 'lp-calendar__time-cell--active': hour === selectedHour }"
              @click="handleSelectHour(hour)"
            >
              {{ hour.toString().padStart(2, '0') }}
            </button>
          </div>
        </div>
        <div class="lp-calendar__time-column">
          <div class="lp-calendar__time-label">{{ minuteLabel }}</div>
          <div class="lp-calendar__time-scroll">
            <button
              v-for="minute in minutes"
              :key="`minute-${minute}`"
              type="button"
              class="lp-calendar__time-cell"
              :class="{ 'lp-calendar__time-cell--active': minute === selectedMinute }"
              @click="handleSelectMinute(minute)"
            >
              {{ minute.toString().padStart(2, '0') }}
            </button>
          </div>
        </div>
        <div class="lp-calendar__time-column">
          <div class="lp-calendar__time-label">{{ secondLabel }}</div>
          <div class="lp-calendar__time-scroll">
            <button
              v-for="second in seconds"
              :key="`second-${second}`"
              type="button"
              class="lp-calendar__time-cell"
              :class="{ 'lp-calendar__time-cell--active': second === selectedSecond }"
              @click="handleSelectSecond(second)"
            >
              {{ second.toString().padStart(2, '0') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.lp-calendar {
  width: 100%;
  padding: 12px 16px 16px;
  background-color: $semanticWhite;
  border-radius: 12px;
  border: 1px solid $borderDefault;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
  display: grid;
  grid-template-columns: minmax(320px, 1fr) minmax(200px, 260px);
  column-gap: 16px;
  margin: 0 auto;
  box-sizing: border-box;

  &--date-only {
    grid-template-columns: minmax(320px, 1fr);
  }

  &__header {
    display: grid;
    grid-template-columns: 1fr auto;
    align-items: center;
    column-gap: 12px;
  }

  &__title {
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: auto;
    column-gap: 4px;
    align-items: center;

    &-text {
      font-size: 18px;
      font-weight: 700;
      color: $textDefault;
    }

    &-icon {
      color: $textDefault;
      font-size: 20px;
    }
  }

  &__nav {
    display: grid;
    grid-template-columns: repeat(2, 36px);
    column-gap: 8px;

    &-button {
      border: 1px solid $borderDefault;
      border-radius: 8px;
      background-color: $semanticWhite;
      color: $textDefault;
      cursor: pointer;
      padding: 6px;
      display: grid;
      place-items: center;

      &:hover {
        border-color: $borderActive;
      }
    }
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(7, minmax(40px, 1fr));
    grid-auto-rows: 44px;
    row-gap: 6px;
    column-gap: 6px;
    align-items: center;
  }

  &__weekday {
    text-align: center;
    font-weight: 700;
    color: $textDefault;

    &--sunday {
      color: $semanticError;
    }

    &--saturday {
      color: $semanticPrimary;
    }
  }

  &__day {
    border: 1px solid transparent;
    border-radius: 10px;
    background-color: $semanticWhite;
    color: $textDefault;
    cursor: pointer;
    font-size: 16px;
    font-weight: 600;
    display: grid;
    place-items: center;
    height: 100%;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;

    &:hover {
      border-color: $borderActive;
      box-shadow: 0 0 0 1px $borderActive inset;
    }

    &--other {
      color: $textDisable;
    }

    &--sunday {
      color: $semanticError;
    }

    &--saturday {
      color: $semanticPrimary;
    }

    &--today {
      border-color: $borderActive;
      box-shadow: 0 0 0 1px $borderActive inset;
    }

    &--selected {
      background-color: $semanticPrimary;
      color: $semanticWhite;
      border-color: $semanticWarning;
      box-shadow: 0 0 0 2px $semanticWarning inset;
    }
  }

  &__footer {
    display: grid;
    grid-template-columns: repeat(2, auto);
    justify-content: space-between;
    align-items: center;
  }

  &__link-button {
    background: none;
    border: none;
    color: $semanticPrimary;
    cursor: pointer;
    font-weight: 700;
    padding: 4px 8px;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }

    &--today {
      justify-self: end;
    }
  }

  &__time {
    display: grid;
    grid-template-rows: auto 1fr;
    row-gap: 8px;
  }

  &__time-title {
    font-weight: 700;
    color: $textDefault;
    font-size: 16px;
  }

  &__time-columns {
    display: grid;
    grid-template-columns: repeat(3, minmax(60px, 1fr));
    column-gap: 8px;
    align-items: center;
  }

  &__time-column {
    display: grid;
    grid-template-rows: auto 1fr;
    row-gap: 6px;
    background: $semanticWhite;
    border-radius: 10px;
    padding: 8px 6px;
    border: 1px solid $borderDefault;
  }

  &__time-label {
    text-align: center;
    font-weight: 700;
    color: $textDefault;
  }

  &__time-scroll {
    display: grid;
    grid-auto-rows: 32px;
    row-gap: 4px;
    overflow-y: auto;
    padding: 4px;
    background: $semanticWhite;
    border: 1px solid $borderDefault;
    border-radius: 8px;
    max-height: 220px;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &__time-cell {
    border: 1px solid transparent;
    border-radius: 8px;
    background-color: $semanticWhite;
    color: $textDefault;
    cursor: pointer;
    font-weight: 700;
    font-size: 14px;
    display: grid;
    place-items: center;
    height: 100%;
    transition: border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease, color 0.2s ease;

    &:hover {
      border-color: $borderActive;
      box-shadow: 0 0 0 1px $borderActive inset;
    }

    &--active {
      background-color: $semanticPrimary;
      color: $semanticWhite;
      border-color: $semanticWarning;
      box-shadow: 0 0 0 2px $semanticWarning inset;
    }
  }
}
</style>
